import { useCallback, useRef, useState } from 'react';
import { App as AntdApp } from 'antd';

import { saveItemCells, isStaleItem } from '../services/items';
import { needsAttestation } from '../services/mfa';
import { getElementValidationErrors } from '../utils/elements';
import { missingRequired } from '../utils/required';
import { normalizeId } from '../utils/ids';
import { sameValue as same } from '../components/items/staleConflict';

/**
 * A card moved on the board (KPR-1395): the item's element written through
 * the table's own write path (`saveItemCells`: `PUT /api/items/{id}` with
 * `merge: true` and `ifUpdatedAt`), so it is an ordinary item update and
 * History records it like any edit.
 *
 *   const board = useBoardMove({ getElementsForCard, onItemUpdated, onItemRemoved });
 *   board.move(item, { status: 'ready' })   // optimistic; resolves when settled
 *   board.pending      itemId -> the item as it will stand while the save runs
 *   board.fill         { item, elements, missing, label } — the item's form
 *                      for the elements the move leaves empty, or null
 *   board.attestation  the card's signature prompt, or null
 *
 * - **Checked first, on the row as it would stand**: the value's own rules
 *   (`getElementValidationErrors`) and `required` / `requiredWhen`
 *   (`utils/required`, the file the API vendors). A missing element opens
 *   `fill` instead of sending anything: the person fills it in and the move
 *   and those values go together (`submitFill`), or cancels and the card
 *   stays where it was (`cancelFill`).
 * - **Optimistic, with rollback**: the card is drawn in its new column while
 *   the save runs; a refusal puts it back and says why in one line.
 * - **Someone else saved meanwhile** (409 `stale_item`, the item as it now
 *   stands in the answer): when the moved elements still read as this board
 *   read them, the move goes again once on the new version — nothing anyone
 *   wrote is overwritten. Otherwise the card lands where it now is and the
 *   board says so.
 * - **A card that asks for a signature** (428): the prompt, then the same
 *   request signed.
 * - A 404 (the item was deleted) takes the card off the board.
 */
export default function useBoardMove({ getElementsForCard, onItemUpdated, onItemRemoved, attachmentsOff = false }) {
	const { message } = AntdApp.useApp();
	const [pending, setPending] = useState({});
	const [fill, setFill] = useState(null);
	const [attestation, setAttestation] = useState(null);
	const pendingRef = useRef(pending);
	pendingRef.current = pending;

	const defsOf = useCallback(
		item => (typeof getElementsForCard === 'function' ? getElementsForCard(item?.card_id) || [] : []),
		[getElementsForCard]
	);

	const send = useCallback(async (item, elements) => {
		const id = normalizeId(item?._id);
		const before = item?.elements || {};
		const names = Object.keys(elements);
		setPending(prev => ({ ...prev, [id]: { ...item, elements: { ...before, ...elements } } }));
		const settle = () => setPending((prev) => {
			const next = { ...prev };
			delete next[id];
			return next;
		});

		const request = (ifUpdatedAt, config) => saveItemCells(id, elements, { ifUpdatedAt: ifUpdatedAt || null, config });
		const signed = ifUpdatedAt => request(ifUpdatedAt).catch((error) => {
			if (!needsAttestation(error)) throw error;
			return new Promise((resolve, reject) => {
				setAttestation({
					mode: error?.response?.data?.mode || 'pin',
					cardId: normalizeId(item?.card_id),
					onSigned: token => request(ifUpdatedAt, { headers: { 'X-Attestation': token } }).then(resolve, reject),
					onCancel: () => reject(error),
				});
			});
		});

		try {
			let saved;
			try {
				saved = await signed(item?.updatedAt);
			} catch (first) {
				const current = isStaleItem(first) ? first?.response?.data?.item : null;
				if (!current || !current._id) throw first;
				// Someone moved the card (or set what it needs) since the board
				// read it: theirs stands, and the card goes where it now is.
				if (names.some(name => !same(current.elements?.[name], before[name]))) {
					settle();
					onItemUpdated?.(current);
					message.warning('Somebody changed this item since the board loaded, so it was not moved. It now shows where it is.');
					return { ok: false, reason: 'stale' };
				}
				// Somebody (or keepr) changed something else: once more, on their version.
				saved = await signed(current.updatedAt);
			}
			settle();
			if (saved && saved._id) onItemUpdated?.(saved);
			return { ok: true, item: saved };
		} catch (error) {
			settle();
			const status = error?.response?.status;
			const data = error?.response?.data;
			if (isStaleItem(error)) {
				const current = data?.item;
				if (current && current._id) onItemUpdated?.(current);
				message.warning('Somebody changed this item since the board loaded, so it was not moved. It now shows where it is.');
				return { ok: false, reason: 'stale' };
			}
			if (needsAttestation(error)) {
				message.info('Not moved — this card asks you to sign each save.');
				return { ok: false, reason: 'attestation' };
			}
			if (status === 404) {
				onItemRemoved?.(id);
				message.warning('This item is no longer here, so it was taken off the board.');
				return { ok: false, reason: 'gone' };
			}
			const said = (typeof data?.message === 'string' && data.message) || (typeof data === 'string' && data) || null;
			let text;
			if (status === 403) text = said || 'You cannot change this item.';
			else if (status === 409 && data?.code === 'item_locked') text = said || 'This item is locked, so it cannot be moved.';
			else text = said || 'Something went wrong, and the item was not moved.';
			message.error(`Not moved. ${text}`);
			return { ok: false, reason: 'refused', message: text };
		}
	}, [message, onItemUpdated, onItemRemoved]);

	/**
	 * Move `item` by writing `elements` (`{ name: value }`), with `label` the
	 * column's name (for the form's title). Resolves `{ ok, reason? }`.
	 */
	const move = useCallback(async (item, elements, { label = '' } = {}) => {
		const id = normalizeId(item?._id);
		if (!id || !elements || !Object.keys(elements).length) return { ok: false, reason: 'nothing' };
		if (pendingRef.current[id]) return { ok: false, reason: 'busy' };
		const defs = defsOf(item);
		const before = item?.elements || {};
		const merged = { ...before, ...elements };
		const own = getElementValidationErrors(defs, merged, { stored: before, attachmentsOff });
		const ownError = Object.keys(elements).map(name => own[name]).find(Boolean);
		if (ownError) {
			message.error(`Not moved. ${ownError}`);
			return { ok: false, reason: 'invalid', message: ownError };
		}
		const missing = missingRequired(defs, merged, attachmentsOff ? { attachments: false } : undefined);
		if (missing.length) {
			setFill({ item, elements, missing, label });
			return { ok: false, reason: 'fill' };
		}
		return send(item, elements);
	}, [defsOf, send, message, attachmentsOff]);

	const cancelFill = useCallback(() => setFill(null), []);

	/** The form's answer: the missing values, sent with the move as one update. */
	const submitFill = useCallback(async (values) => {
		const open = fill;
		if (!open) return { ok: false };
		setFill(null);
		return send(open.item, { ...open.elements, ...(values || {}) });
	}, [fill, send]);

	return { move, pending, fill, cancelFill, submitFill, attestation, setAttestation };
}
